import { cn } from '@/lib/utils'
import { ButtonHTMLAttributes, forwardRef } from 'react'

/**
 * Flat 32×32 icon button shared by every list-table row in the app
 * (TaskTable, AssetSetTable, and the legacy DataTable column defs).
 *
 * @example
 * ```tsx
 * <RowActionButton tone="accent" title="View" onClick={...}>
 *   <EyeIcon className="h-3.5 w-3.5" />
 * </RowActionButton>
 * ```
 */
export type RowActionTone = 'accent' | 'success' | 'danger' | 'warning'

export interface RowActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: RowActionTone
}

const TONE_HOVER: Record<RowActionTone, string> = {
  accent: 'hover:text-accent',
  success: 'hover:text-success hover:border-success/40',
  danger: 'hover:text-danger hover:border-danger/40',
  warning: 'hover:text-warning hover:border-warning/40',
}

/** Exported for FlyoutMenu / other wrappers that can't use RowActionButton directly. */
export const ROW_ACTION_BTN_CLASS =
  'border-border-strong bg-panel-2 text-text-dim grid h-8 w-8 place-items-center rounded-sm border transition-colors duration-150 hover:bg-panel-3 hover:text-text hover:border-accent-tint disabled:opacity-50 disabled:pointer-events-none'

const BASE = ROW_ACTION_BTN_CLASS

/** Per-tone hover-color classes; exported for the FlyoutMenu wrappers. */
export const ROW_ACTION_TONE_HOVER = TONE_HOVER

export const RowActionButton = forwardRef<
  HTMLButtonElement,
  RowActionButtonProps
>(({ tone = 'accent', className, type = 'button', ...rest }, ref) => {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(BASE, TONE_HOVER[tone], className)}
      {...rest}
    />
  )
})
RowActionButton.displayName = 'RowActionButton'

export default RowActionButton
